import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    whatsappNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    deadlineReminders: true,
    riskAlerts: true,
    reportGeneration: true,
    systemUpdates: false
  });

  const [reminderSettings, setReminderSettings] = useState({
    deadlineAdvance: '7',
    riskThreshold: 'medium',
    notificationTime: '09:00',
    weekendNotifications: false
  });

  const [contactSettings, setContactSettings] = useState({
    email: 'compliance@facilitypro.com',
    whatsapp: '+91 98765 43210',
    sms: '+91 98765 43210'
  });

  const deadlineOptions = [
    { value: '1', label: '1 day before' },
    { value: '3', label: '3 days before' },
    { value: '7', label: '7 days before' },
    { value: '15', label: '15 days before' },
    { value: '30', label: '30 days before' }
  ];

  const riskThresholdOptions = [
    { value: 'low', label: 'Low and above' },
    { value: 'medium', label: 'Medium and above' },
    { value: 'high', label: 'High only' }
  ];

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleReminderChange = (key, value) => {
    setReminderSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleContactChange = (key, value) => {
    setContactSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = () => {
    console.log('Saving notification settings:', { settings, reminderSettings, contactSettings });
  };

  const handleTestNotification = (type) => {
    console.log(`Sending test ${type} notification`);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Bell" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Notification Settings</h3>
            <p className="text-sm text-muted-foreground">Configure alerts and reminders</p>
          </div>
        </div>
        <Button variant="default" onClick={handleSaveSettings} iconName="Save" iconPosition="left">
          Save Settings
        </Button>
      </div>

      <div className="space-y-8">
        {/* Notification Channels */}
        <div>
          <h4 className="font-medium text-foreground mb-4">Notification Channels</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <Checkbox
                label="Email Notifications"
                description="Receive alerts via email"
                checked={settings.emailNotifications}
                onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
              />
              
              <Checkbox
                label="WhatsApp Notifications"
                description="Get instant messages on WhatsApp"
                checked={settings.whatsappNotifications}
                onChange={(e) => handleSettingChange('whatsappNotifications', e.target.checked)}
              />
            </div>
            
            <div className="space-y-4">
              <Checkbox
                label="SMS Notifications"
                description="Receive text messages"
                checked={settings.smsNotifications}
                onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
              />
              
              <Checkbox
                label="Push Notifications"
                description="Browser push notifications"
                checked={settings.pushNotifications}
                onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
              />
            </div>
          </div>
        </div>

        {/* Notification Types */}
        <div>
          <h4 className="font-medium text-foreground mb-4">Notification Types</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <Checkbox
                label="Deadline Reminders"
                description="Alerts for upcoming compliance deadlines"
                checked={settings.deadlineReminders}
                onChange={(e) => handleSettingChange('deadlineReminders', e.target.checked)}
              />
              
              <Checkbox
                label="Risk Alerts"
                description="Notifications for compliance risks"
                checked={settings.riskAlerts}
                onChange={(e) => handleSettingChange('riskAlerts', e.target.checked)}
              />
            </div>
            
            <div className="space-y-4">
              <Checkbox
                label="Report Generation"
                description="Updates on report generation status"
                checked={settings.reportGeneration}
                onChange={(e) => handleSettingChange('reportGeneration', e.target.checked)}
              />
              
              <Checkbox
                label="System Updates"
                description="Information about system changes"
                checked={settings.systemUpdates}
                onChange={(e) => handleSettingChange('systemUpdates', e.target.checked)}
              />
            </div>
          </div>
        </div>

        {/* Reminder Settings */}
        <div>
          <h4 className="font-medium text-foreground mb-4">Reminder Settings</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Deadline Advance Notice"
              description="How early to send deadline reminders"
              options={deadlineOptions}
              value={reminderSettings.deadlineAdvance}
              onChange={(value) => handleReminderChange('deadlineAdvance', value)}
            />
            
            <Select
              label="Risk Alert Threshold"
              description="Minimum risk level for alerts"
              options={riskThresholdOptions}
              value={reminderSettings.riskThreshold}
              onChange={(value) => handleReminderChange('riskThreshold', value)}
            />
            
            <Input
              label="Notification Time"
              description="Preferred time for daily notifications"
              type="time"
              value={reminderSettings.notificationTime}
              onChange={(e) => handleReminderChange('notificationTime', e.target.value)}
            />
          </div>
          
          <div className="mt-4">
            <Checkbox
              label="Weekend Notifications"
              description="Receive notifications on weekends"
              checked={reminderSettings.weekendNotifications}
              onChange={(e) => handleReminderChange('weekendNotifications', e.target.checked)}
            />
          </div>
        </div>

        {/* Contact Information */}
        <div>
          <h4 className="font-medium text-foreground mb-4">Contact Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Email Address"
              type="email"
              value={contactSettings.email}
              onChange={(e) => handleContactChange('email', e.target.value)}
              disabled={!settings.emailNotifications}
            />
            
            <Input
              label="WhatsApp Number"
              type="tel"
              value={contactSettings.whatsapp}
              onChange={(e) => handleContactChange('whatsapp', e.target.value)}
              disabled={!settings.whatsappNotifications}
            />
            
            <Input
              label="SMS Number"
              type="tel"
              value={contactSettings.sms}
              onChange={(e) => handleContactChange('sms', e.target.value)}
              disabled={!settings.smsNotifications}
            />
          </div>
        </div>

        {/* Test Notifications */}
        <div>
          <h4 className="font-medium text-foreground mb-4">Test Notifications</h4>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleTestNotification('email')}
              disabled={!settings.emailNotifications}
              iconName="Mail"
              iconPosition="left"
            >
              Test Email
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleTestNotification('whatsapp')}
              disabled={!settings.whatsappNotifications}
              iconName="MessageCircle"
              iconPosition="left"
            >
              Test WhatsApp
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleTestNotification('sms')}
              disabled={!settings.smsNotifications}
              iconName="Smartphone"
              iconPosition="left"
            >
              Test SMS
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleTestNotification('push')}
              disabled={!settings.pushNotifications}
              iconName="Bell"
              iconPosition="left"
            >
              Test Push
            </Button>
          </div>
        </div>

        {/* Information Box */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={16} className="text-primary mt-0.5" />
            <div className="text-sm">
              <p className="text-foreground font-medium mb-1">Notification Guidelines</p>
              <ul className="text-muted-foreground space-y-1">
                <li>• Critical compliance deadlines will always generate notifications regardless of settings</li>
                <li>• WhatsApp notifications require phone number verification</li>
                <li>• Email notifications may take up to 5 minutes to deliver</li>
                <li>• Test notifications help verify your contact information</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;